import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TopicsBar Component
 *
 * Displays contextual topic suggestions fetched from the REST API.
 * Supports RTL layouts and tracks analytics events.
 *
 * @package WooAIChatbot
 * @author Roman Siromskyi <leowebmark@gmail.com>
 */
import { useEffect, useState, useRef } from 'react';
import topicsService from '../../services/topicsService';
import { wac } from '../utils/wacClassNames';
/**
 * Get RTL setting from WordPress globals
 */
const getIsRTL = () => {
    const wooAI = window.wooAiChatbot || window.wooAIChatbot;
    return wooAI?.isRTL === true || wooAI?.isRTL === 'true';
};
/**
 * TopicsBar Component
 *
 * Fetches and displays contextual topics from the REST API.
 * Tracks impressions and clicks for analytics.
 */
export function TopicsBar({ onTopicClick }) {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const impressionTrackedRef = useRef(false);
    // Fetch topics on mount
    useEffect(() => {
        const fetchTopics = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await topicsService.fetchTopicsWithContext();
                setTopics(response.topics || []);
            }
            catch (err) {
                console.error('Failed to fetch topics:', err);
                setError(err instanceof Error ? err.message : 'Failed to load topics');
                setTopics([]);
            }
            finally {
                setLoading(false);
            }
        };
        fetchTopics();
    }, []);
    // Track topic impressions when topics are loaded and displayed
    useEffect(() => {
        if (topics.length > 0 && !impressionTrackedRef.current) {
            impressionTrackedRef.current = true;
            // Track impression for each topic
            window.WooAIChatbotAnalytics?.track('topic_impression', {
                topic_count: topics.length,
                topic_ids: topics.map((t) => t.id).join(','),
                topic_names: topics.map((t) => t.name).join(','),
            });
        }
    }, [topics]);
    /**
     * Handle topic click
     */
    const handleTopicClick = (topic) => {
        // Track click event
        window.WooAIChatbotAnalytics?.track('topic_click', {
            topic_id: topic.id,
            topic_slug: topic.slug,
            topic_name: topic.name,
        });
        // Build message from topic - use first sample question or topic name as query
        const message = topic.sampleQuestions && topic.sampleQuestions.length > 0
            ? topic.sampleQuestions[0]
            : topic.name.replace(/^[^\p{L}]+/u, '').trim();
        // Invoke callback
        onTopicClick?.(topic, message);
    };
    // Don't render if loading, error, or no topics
    if (loading) {
        return null;
    }
    if (error || topics.length === 0) {
        return null;
    }
    return (_jsx("div", { className: wac('border-t flex flex-wrap items-center justify-center gap-2'), style: {
            borderColor: 'var(--waa-gray-200)',
            padding: '8px 16px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
        }, dir: getIsRTL() ? 'rtl' : 'ltr', children: topics.map((topic) => (_jsxs("button", { type: "button", onClick: () => handleTopicClick(topic), className: wac('flex-shrink-0 rounded-full border hover:border-[var(--waa-primary)] hover:bg-[var(--waa-primary-50)] hover:text-[var(--waa-primary)] transition-all text-sm font-medium flex items-center gap-1 bg-white'), style: {
                borderColor: 'var(--waa-gray-300)',
                padding: '4px 12px',
                height: '32px',
                whiteSpace: 'nowrap',
                maxWidth: '200px',
                cursor: 'pointer',
            }, children: [topic.icon && _jsx("span", { style: { fontSize: '14px' }, children: topic.icon }), _jsx("span", { style: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: topic.name })] }, topic.id))) }));
}
